/**
 * Owner: Masa Bayu
 * File: dropcap-justified-face-object.tsx
 * Date:Time: 2026-10-12 19:45
 * Description: Fasa 7 · dropcap justify · Stage-2b constrained path when flag ON.
 */

import type { CSSProperties, ReactElement } from 'react';
import { groupInstructionsIntoLines, JustifiedTextBlock } from './JustifiedTextBlock';
import {
  buildJustificationPlan,
  estimateColumnWidth,
} from './build-justification-data';
import { faceJustifyInstructions } from '../paint/face-justify-remeasure';
import { measureSpacePt } from '../paint/face-justify-remeasure';
import { nativeLineHeightPt } from './native-line-metrics';
import { FACE_BODY_INK, faceStackZIndex } from './qx-editor-page-face-objects';
import { resolveCssFontFamily } from '../fonts/spsu-fonts';
import type { NativeDropCapPair } from './spsu-native-dropcap-face';
import type { AssetLibrary, TextElement } from '../extract/spsu-types';

type Props = {
  readonly pair: NativeDropCapPair;
  readonly pageTexts: readonly TextElement[];
  /** Fasa 8 · full paragraph group (letter + body lines). */
  readonly paragraphPlates?: readonly TextElement[];
  readonly assets: AssetLibrary;
};

const PX_PER_PT = 96 / 72;

function measureGlyphWidthPt(text: string, fontCss: string): number {
  if (typeof document === 'undefined') return 0;
  const ctx = document.createElement('canvas').getContext('2d');
  if (!ctx) return 0;
  ctx.font = fontCss;
  return ctx.measureText(text).width / PX_PER_PT;
}

/**
 * Face-only dropcap justify · letter absolute · body lines per constraint.
 * Wrap lines indented; full-column lines under letter · no paint-through.
 */
export function DropCapJustifiedFaceObject({
  pair,
  pageTexts,
  paragraphPlates,
  assets,
}: Props): ReactElement | null {
  const { letter, host } = pair;
  const unit = host.bbox.unit;
  const columnLeft = Math.min(letter.bbox.x, host.bbox.x);
  const top = Math.min(letter.bbox.y, host.bbox.y);
  const bboxInset = Math.max(0, host.bbox.x - columnLeft);

  const absorbEls = pair.absorbIds
    .map((id) => pageTexts.find((t) => t.id === id))
    .filter((el): el is TextElement => el != null);
  const planPlates = paragraphPlates ?? [letter, host, ...absorbEls];
  const columnWidth = estimateColumnWidth(pageTexts, pair, columnLeft);
  const plan = buildJustificationPlan(planPlates, columnWidth);
  if (plan.instructions.length === 0 || plan.dropcap == null) return null;

  const letterFamily = resolveCssFontFamily(letter.style.fontFamily, assets);
  const hostFamily = resolveCssFontFamily(host.style.fontFamily, assets);
  const hostFontCss = [
    host.style.fontStyle === 'italic' ? 'italic' : 'normal',
    host.style.fontWeight === 'normal' ? '400' : String(host.style.fontWeight),
    `${host.style.fontSize}pt`,
    hostFamily,
  ].join(' ');
  const letterFontCss = [
    letter.style.fontStyle === 'italic' ? 'italic' : 'normal',
    letter.style.fontWeight === 'normal' ? '700' : String(letter.style.fontWeight),
    `${letter.style.fontSize}pt`,
    letterFamily,
  ].join(' ');

  const glyphInset = measureGlyphWidthPt(letter.content.trim(), letterFontCss);
  const pad = Math.max(measureSpacePt(hostFontCss) * 0.35, 0.75);
  const inset = Math.max(bboxInset, glyphInset + pad);
  const flowNarrow = Math.max(1, columnWidth - inset);

  const wrapBeside = 1 + pair.absorbIds.length;
  const constraints = plan.flowPlates.map((_, i) =>
    i < wrapBeside ? flowNarrow : columnWidth,
  );
  // Constrained-C removed (B3) · legacy remasure via faceJustifyInstructions.
  const justified = faceJustifyInstructions(
    plan.tokens,
    plan.instructions,
    constraints,
    columnWidth,
    hostFontCss,
  );
  const lines = groupInstructionsIntoLines(justified);

  const bodyPlates = planPlates.filter((p) => p.id !== letter.id);
  const bodyLine = nativeLineHeightPt(bodyPlates.length > 0 ? bodyPlates : planPlates);
  const lineHeightCss = bodyLine / Math.max(1, host.style.fontSize);
  const letterBoxH = Math.max(
    letter.style.fontSize * 0.92,
    bodyLine * Math.min(2.05, wrapBeside + 0.15),
  );

  const box: CSSProperties = {
    position: 'absolute',
    left: `${columnLeft}${unit}`,
    top: `${top}${unit}`,
    width: `${columnWidth}${unit}`,
    zIndex: Math.max(faceStackZIndex(letter), faceStackZIndex(host)),
    boxSizing: 'border-box',
    margin: 0,
    padding: 0,
    overflow: 'visible',
    fontFamily: hostFamily,
    fontSize: `${host.style.fontSize}${unit}`,
    fontWeight: host.style.fontWeight,
    fontStyle: host.style.fontStyle,
    color: FACE_BODY_INK,
    lineHeight: lineHeightCss,
  };

  return (
    <div
      className="qx-native-dropcap-justified"
      data-native-dropcap="true"
      data-native-dropcap-justified="true"
      data-letter-id={letter.id}
      data-host-id={host.id}
      data-absorb-ids={pair.absorbIds.join(',')}
      data-qxeditor-element={host.id}
      style={box}
    >
      <span
        className="qx-native-dropcap-letter"
        data-native-dropcap-letter=""
        data-element-id={letter.id}
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: `${inset}${unit}`,
          height: `${letterBoxH}${unit}`,
          fontFamily: letterFamily,
          fontSize: `${letter.style.fontSize}${unit}`,
          fontWeight: letter.style.fontWeight === 'normal' ? 700 : letter.style.fontWeight,
          fontStyle: letter.style.fontStyle,
          lineHeight: 0.9,
          color: FACE_BODY_INK,
          margin: 0,
          padding: 0,
          float: 'none',
          display: 'block',
          zIndex: 2,
          pointerEvents: 'none',
        }}
      >
        {letter.content.trim()}
      </span>
      <div
        className="qx-native-dropcap-justified-flow"
        data-native-dropcap-justified-flow=""
        style={{ position: 'relative', zIndex: 1 }}
      >
        {lines.map((lineInst, lineIndex) => {
          const narrow = lineIndex < wrapBeside;
          return (
            <div
              key={`drop-line-${lineIndex}`}
              style={{
                marginLeft: narrow ? `${inset}${unit}` : 0,
                width: narrow ? `${flowNarrow}${unit}` : `${columnWidth}${unit}`,
              }}
            >
              <JustifiedTextBlock
                instructions={lineInst.map((inst, i) => ({
                  ...inst,
                  isLastInLine: i === lineInst.length - 1,
                }))}
                spacingUnit="pt"
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
